import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Scale, Brain, Settings, TrendingUp, CheckCircle2, Mail, Linkedin, ArrowRight, Building2, Users, Award, Globe, Sparkles, UserCircle, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import ConsentBanner from '../components/ConsentBanner';
import PasswordModal from '../components/PasswordModal';
import {
  heroData,
  servicesData,
  whyMeData,
  aboutData,
  competenciesData,
  referencesData,
  contactData
} from '../mock/data';

const LandingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    organizationType: '',
    message: ''
  });

  const [scrolled, setScrolled] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState('/portfolio');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, organizationType: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast.success('Vielen Dank für Ihre Nachricht! Ich melde mich in Kürze bei Ihnen.');
    setFormData({ name: '', email: '', organization: '', organizationType: '', message: '' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const iconMap = {
    scale: Scale,
    brain: Brain,
    settings: Settings,
    'trending-up': TrendingUp
  };

  return (
    <div className="landing-page">
      {/* Consent Banner */}
      <ConsentBanner />
      
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-accent-green/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-primary-light/5 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {/* Header / Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-effect shadow-lg' : 'bg-white/95'
      } backdrop-blur-sm border-b border-gray-200`}>
        <div className="container mx-auto px-6 py-4">
          <nav className="flex items-center justify-between">
            <div className="text-2xl font-bold text-primary-dark flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-accent-green" />
              Sofia Laitenberger
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="nav-link">Leistungen</button>
              <button onClick={() => scrollToSection('about')} className="nav-link">Über mich</button>
              <button onClick={() => scrollToSection('competencies')} className="nav-link">Kompetenzen</button>
              <button onClick={() => scrollToSection('references')} className="nav-link">Referenzen</button>
              <Button onClick={() => scrollToSection('contact')} className="modern-button">
                Kontakt
              </Button>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section-modern pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="container mx-auto max-w-7xl relative z-10">
          {/* Full Width Headline */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-block mb-4 px-4 py-2 glass-badge">
              <span className="text-accent-green font-semibold text-sm">✨ Recht × KI × Compliance</span>
            </div>
            <h1 className="hero-headline-fullwidth mb-3">
              {heroData.headlinePart1}
            </h1>
            <h2 className="hero-headline-fullwidth-secondary">
              {heroData.headlinePart2}
            </h2>
          </div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="hero-content animate-slide-in-left">
              <p className="hero-subline-modern mb-8">
                {heroData.subline}
              </p>
              <ul className="space-y-3 mb-10">
                {heroData.bulletPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="icon-glow">
                      <CheckCircle2 className="w-6 h-6 text-accent-green flex-shrink-0 mt-0.5" />
                    </div>
                    <span className="text-lg text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToSection('contact')} 
                  size="lg" 
                  className="modern-button-primary"
                >
                  Mit mir zusammenarbeiten
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  onClick={() => {
                    setModalTarget('/lebenslauf');
                    setIsPasswordModalOpen(true);
                  }}
                  className="modern-button-outline"
                >
                  Profil für Arbeitgeber
                </Button>
              </div>
            </div>
            <div className="hero-image-container-modern animate-slide-in-right">
              <div className="hero-image-wrapper-modern">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-green/20 to-primary-dark/20 rounded-2xl blur-2xl"></div>
                <img 
                  src={heroData.image} 
                  alt="Sofia Laitenberger"
                  className="hero-image-modern"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section-modern py-20 px-6 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="section-title-modern mb-4">Was ich anbiete</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Maßgeschneiderte Lösungen für Ihre digitale Transformation
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {/* For Companies */}
            <Card className="service-card-modern group">
              <CardHeader>
                <div className="icon-container-modern mb-4">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-primary-dark mb-2">{servicesData.forCompanies.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {servicesData.forCompanies.services.map((service, index) => (
                  <div key={index} className="service-item-modern">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent-green flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-lg text-primary-dark mb-1">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* For Private Clients */}
            <Card className="service-card-modern group">
              <CardHeader>
                <div className="icon-container-modern mb-4">
                  <UserCircle className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-primary-dark mb-2">{servicesData.forPrivateClients.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {servicesData.forPrivateClients.services.map((service, index) => (
                  <div key={index} className="service-item-modern">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent-green flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-lg text-primary-dark mb-1">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* For Employers */}
            <Card className="service-card-modern group">
              <CardHeader>
                <div className="icon-container-modern mb-4">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-primary-dark mb-2">{servicesData.forEmployers.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {servicesData.forEmployers.services.map((service, index) => (
                  <div key={index} className="service-item-modern">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent-green flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-lg text-primary-dark mb-1">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* For Law Firms */}
            <Card className="service-card-modern group">
              <CardHeader>
                <div className="icon-container-modern mb-4">
                  <Scale className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl text-primary-dark mb-2">{servicesData.forLawFirms.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {servicesData.forLawFirms.services.map((service, index) => (
                  <div key={index} className="service-item-modern">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-accent-green flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-semibold text-lg text-primary-dark mb-1">{service.title}</h3>
                        <p className="text-gray-600">{service.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Me Section */}
      <section className="why-section-modern py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-modern"></div>
        <div className="container mx-auto max-w-7xl relative z-10">
          <h2 className="section-title-modern text-center mb-16 text-white">{whyMeData.title}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMeData.points.map((point, index) => (
              <div key={index} className="why-card-modern">
                <div className="icon-badge-modern mb-4">
                  <Award className="w-6 h-6 text-accent-green" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{point.title}</h3>
                <p className="text-gray-200">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section-modern py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="section-title-modern text-center mb-12">{aboutData.title}</h2>
          <div className="glass-content-box">
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              {aboutData.story.map((paragraph, index) => (
                <p key={index} className="animate-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Competencies Section */}
      <section id="competencies" className="competencies-section-modern py-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="section-title-modern mb-4">Kompetenzen & Schwerpunkte</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Expertise an der Schnittstelle von Recht, Technologie und Management
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(competenciesData).map(([key, category], index) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Card key={key} className="competency-card-modern">
                  <CardHeader>
                    <div className="icon-container-modern-alt mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl text-primary-dark">{category.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {category.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-accent-green flex-shrink-0 mt-1" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* References Section */}
      <section id="references" className="references-section-modern py-20 px-6 relative">
        <div className="container mx-auto max-w-7xl">
          <h2 className="section-title-modern text-center mb-6">{referencesData.title}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto text-lg">
            Vertrauen durch Erfahrung in renommierten Institutionen
          </p>
          
          {/* Institutions */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {referencesData.institutions.map((institution, index) => (
              <div key={index} className="institution-badge-modern">
                {institution}
              </div>
            ))}
          </div>

          {/* Brand Portfolio CTA */}
          <div className="text-center mb-16">
            <Button 
              size="lg" 
              onClick={() => {
                setModalTarget('/portfolio');
                setIsPasswordModalOpen(true);
              }}
              className="bg-accent-green hover:bg-accent-light text-white font-semibold px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Brand Portfolio
            </Button>
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {referencesData.testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card-modern">
                <CardContent className="pt-6">
                  <div className="mb-4">
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Sparkles key={i} className="w-4 h-4 text-accent-green fill-accent-green" />
                      ))}
                    </div>
                    <p className="text-gray-700 italic">"{testimonial.text}"</p>
                  </div>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-primary-dark">{testimonial.author}</p>
                    <p className="text-sm text-gray-600">{testimonial.position}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* LinkedIn Button */}
          <div className="text-center">
            <a 
              href={heroData.linkedinUrl} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" className="modern-button-outline-large">
                <Linkedin className="mr-2 w-5 h-5" />
                Mehr zu mir auf LinkedIn
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section-modern py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-modern"></div>
        <div className="container mx-auto max-w-3xl relative z-10">
          <h2 className="section-title-modern text-center mb-6 text-white">{contactData.title}</h2>
          <p className="text-center text-xl text-gray-200 mb-12">
            {contactData.intro}
          </p>

          <Card className="contact-card-modern">
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-primary-dark font-semibold">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Ihr Name"
                      className="mt-2 modern-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-primary-dark font-semibold">E-Mail *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="ihre.email@beispiel.de"
                      className="mt-2 modern-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="organization" className="text-primary-dark font-semibold">Organisation</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleInputChange}
                    placeholder="Ihre Organisation / Unternehmen"
                    className="mt-2 modern-input"
                  />
                </div>

                <div>
                  <Label htmlFor="organizationType" className="text-primary-dark font-semibold">Ich bin *</Label>
                  <Select value={formData.organizationType} onValueChange={handleSelectChange} required>
                    <SelectTrigger className="mt-2 modern-input">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactData.organizationTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-primary-dark font-semibold">Nachricht *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Ihre Nachricht an mich..."
                    rows={6}
                    className="mt-2 modern-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full modern-button-submit"
                >
                  Nachricht senden
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                  <a 
                    href={`mailto:${contactData.email}`} 
                    className="flex items-center gap-2 text-primary-dark hover:text-accent-green transition-all duration-300 font-medium"
                  >
                    <Mail className="w-5 h-5" />
                    <span>{contactData.email}</span>
                  </a>
                  <a 
                    href={contactData.linkedinUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary-dark hover:text-accent-green transition-all duration-300 font-medium"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span>LinkedIn Profil</span>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-modern py-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
            <div className="text-center md:text-left">
              <p className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent-green" />
                Sofia Laitenberger
              </p>
              <p className="text-gray-400">Juristin | KI & Compliance Consultant</p>
            </div>
            <div className="flex items-center gap-3 text-gray-400">
              <Globe className="w-5 h-5" />
              <span>Hamburg / Lissabon</span>
            </div>
            <div className="text-center md:text-right text-sm text-gray-400">
              <p>&copy; 2026 Sofia Laitenberger. Alle Rechte vorbehalten.</p>
            </div>
          </div>
          
          {/* Footer Links */}
          <div className="flex justify-center gap-8 pt-6 border-t border-gray-700">
            <a 
              href="/impressum" 
              className="text-gray-400 hover:text-accent-green transition-colors duration-300 text-sm font-medium"
            >
              Impressum
            </a>
            <a 
              href="/datenschutz" 
              className="text-gray-400 hover:text-accent-green transition-colors duration-300 text-sm font-medium"
            >
              Datenschutzerklärung
            </a>
          </div>
        </div>
      </footer>

      {/* Password Modal */}
      <PasswordModal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)}
        targetPath={modalTarget}
      />
    </div>
  );
};

export default LandingPage;
